import { useEffect, useRef, useState } from 'react';
import { AnimationType } from '../../types/content-components.types.js';

/**
 * Hook to handle component animations on scroll into view
 * Uses Intersection Observer for performance
 */
export const useComponentAnimation = (animationType?: AnimationType) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(animationType === 'none' || !animationType);

  useEffect(() => {
    // If no animation or animation is 'none', show immediately
    if (animationType === 'none' || !animationType) {
      setIsVisible(true);
      return;
    }

    // Check if element is already in viewport on mount (prevents flicker)
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const isInViewport = (
        rect.top >= -50 && // 50px buffer above
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + 50
      );
      
      if (isInViewport) {
        // Already visible, show immediately without flicker
        setIsVisible(true);
        return;
      }
    }

    // Not in viewport yet, set up intersection observer
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, stop observing
          if (ref.current) {
            observer.unobserve(ref.current);
          }
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of component is visible
        rootMargin: '100px', // Start animation well before entering viewport
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [animationType]);

  return { ref, isVisible };
};

